/* eslint-disable react/forbid-prop-types */
// Shared "how to deliver this payment link" fields — the Stripe-not-connected
// alert + Email/WhatsApp checkboxes (with readiness reasons) + the recipient
// text fields shown once a channel is checked. Extracted from
// CreateSendPaymentLinkDialog.jsx so the ad-hoc "Create payment link" page
// (CreateStripePaymentPage.jsx) can reuse the exact same UI + readiness
// semantics instead of a second implementation drifting out of sync.
import PropTypes from 'prop-types';
import {
    FormControlLabel,
    Checkbox,
    TextField,
    Typography,
    Alert,
    Stack,
} from '@mui/material';

export default function PaymentLinkChannelFields({
    readiness,
    emailChecked,
    onEmailCheckedChange,
    whatsappChecked,
    onWhatsappCheckedChange,
    email,
    onEmailChange,
    phone,
    onPhoneChange,
    emailError,
    whatsappError,
}) {
    // Each channel is its own Stack (not just a Fragment) so the checkbox,
    // its readiness reason, and the recipient field always land on their own
    // line — relying on a *caller's* Stack/flex wrapper to blockify these
    // (some of which default to inline elements, e.g. Typography
    // variant="caption" renders a <span>) let a reason run straight into the
    // next channel's checkbox label when this component was dropped into a
    // plain Box (see CreateStripePaymentPage.jsx "Send via" section).
    return (
        <Stack spacing={2}>
            {readiness.stripeConnected === false && (
                <Alert severity="info" data-testid="stripe-not-connected-alert">
                    {readiness.stripeReason}
                </Alert>
            )}

            <Stack spacing={0.5}>
                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={emailChecked}
                            disabled={readiness.email.blocking}
                            onChange={(e) => onEmailCheckedChange(e.target.checked)}
                        />
                    )}
                    label="Email"
                    data-testid="channel-email-checkbox"
                />
                {readiness.email.reason && (
                    <Typography
                        variant="caption"
                        component="div"
                        color={readiness.email.blocking ? 'error' : 'text.secondary'}
                        data-testid="channel-email-reason"
                    >
                        {readiness.email.reason}
                    </Typography>
                )}
                {emailChecked && (
                    <TextField
                        label="Recipient email"
                        size="small"
                        fullWidth
                        value={email}
                        onChange={(e) => onEmailChange(e.target.value)}
                        error={Boolean(emailError)}
                        helperText={emailError}
                    />
                )}
            </Stack>

            <Stack spacing={0.5}>
                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={whatsappChecked}
                            disabled={readiness.whatsapp.blocking}
                            onChange={(e) => onWhatsappCheckedChange(e.target.checked)}
                        />
                    )}
                    label="WhatsApp"
                    data-testid="channel-whatsapp-checkbox"
                />
                {readiness.whatsapp.reason && (
                    <Typography
                        variant="caption"
                        component="div"
                        color={readiness.whatsapp.blocking ? 'error' : 'text.secondary'}
                        data-testid="channel-whatsapp-reason"
                    >
                        {readiness.whatsapp.reason}
                    </Typography>
                )}
                {whatsappChecked && (
                    <TextField
                        label="Recipient phone"
                        size="small"
                        fullWidth
                        value={phone}
                        onChange={(e) => onPhoneChange(e.target.value)}
                        error={Boolean(whatsappError)}
                        helperText={whatsappError}
                    />
                )}
            </Stack>
        </Stack>
    );
}

PaymentLinkChannelFields.propTypes = {
    readiness: PropTypes.object.isRequired,
    emailChecked: PropTypes.bool.isRequired,
    onEmailCheckedChange: PropTypes.func.isRequired,
    whatsappChecked: PropTypes.bool.isRequired,
    onWhatsappCheckedChange: PropTypes.func.isRequired,
    email: PropTypes.string,
    onEmailChange: PropTypes.func.isRequired,
    phone: PropTypes.string,
    onPhoneChange: PropTypes.func.isRequired,
    emailError: PropTypes.string,
    whatsappError: PropTypes.string,
};

PaymentLinkChannelFields.defaultProps = {
    email: '',
    phone: '',
    emailError: null,
    whatsappError: null,
};
